import { useState } from 'react'
import { ClipboardList } from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { ChannelToggle, type Channel } from '../components/ChannelToggle'
import { VideoGuidePanel, type VideoGuide } from '../components/VideoGuidePanel'

const SOCIAL_STEPS = [
  {
    step: 1,
    title: 'Finalise Your Media Plan in MAP',
    items: [
      <>Use the template: <strong className="text-slate-800">MSWW - Ford - EU18 Digital (Social) Template</strong></>,
      <>Do not use local, historic, or custom templates</>,
      <>Complete all required fields — no empty cells, no placeholders (TBC, TBD, N/A, or dashes)</>,
    ],
  },
  {
    step: 2,
    title: 'Review the Plan in the Plan Approval Tab',
    items: [
      <>Go to the Plan Approval tab in Content Tracker</>,
      <>Filter by your market and review the imported plan</>,
      <>If errors: return to MAP, correct the plan, wait for next import <span className="text-slate-400">(runs once daily, every morning)</span></>,
      <>If no errors: review the row count and click <strong className="text-slate-800">Approve</strong></>,
    ],
  },
  {
    step: 3,
    title: 'Validate the Created Rows',
    items: [
      <>Go to the Content Tracker tab and filter by your Plan ID</>,
      <>Confirm the correct number of rows were created</>,
      <>Your responsibility ends here — Creative Agency takes over</>,
    ],
  },
]

const SEARCH_STEPS = [
  {
    step: 1,
    title: 'Finalise Your Media Plan in MAP',
    items: [
      <>Use the template: <strong className="text-slate-800">MSWW - Ford - EU18 Digital (Search) Template</strong></>,
      <>Do not use local, historic, or custom templates</>,
      <>Complete all required fields — no empty cells, no placeholders (TBC, TBD, N/A, or dashes)</>,
    ],
  },
  {
    step: 2,
    title: 'Review the Plan in the Plan Approval Tab',
    items: [
      <>Go to the Plan Approval tab in Content Tracker</>,
      <>Filter by your market and review the imported plan</>,
      <>If errors: return to MAP, correct the plan, wait for next import <span className="text-slate-400">(runs twice daily)</span></>,
      <>If no errors: review the row count and click <strong className="text-slate-800">Approve</strong></>,
    ],
  },
  {
    step: 3,
    title: 'Validate the Created Rows',
    items: [
      <>Go to the Content Tracker tab and filter by your Plan ID</>,
      <>Confirm the correct number of rows was created</>,
      <>Your responsibility ends here — Creative Agency takes over</>,
    ],
  },
]

const SOCIAL_LIVE_STEPS = [
  {
    step: 1,
    title: 'Receive Update Request',
    items: [
      <>Once notified by a client requesting updates to a live campaign, find the campaign in question in the Content Tracker</>,
      <>You will also be notified in the tool if you have been tagged in any comments in the "Asset Feedback" section by the Client or the Creative Agency</>,
    ],
  },
  {
    step: 2,
    title: 'Brief the Activation Team',
    items: [
      <>Brief the Activation team via Wrike on which campaign needs to be updated, referencing the <strong className="text-slate-800">Campaign ID</strong></>,
      <>They will also be notified via the daily Teams summary, which shows all campaigns that are ready for activation</>,
    ],
  },
]

const SEARCH_LIVE_STEPS = [
  {
    step: 1,
    title: 'Receive Update Request',
    items: [
      <>Once notified by a client requesting updates to a live campaign, find the campaign in question in the Content Tracker</>,
      <>You will also be notified in the tool if you have been tagged in any comments in the "Asset Feedback" section by the Client or the Creative Agency</>,
    ],
  },
  {
    step: 2,
    title: 'Brief the Activation Team',
    items: [
      <>Brief the Activation team via Wrike on which campaign needs to be updated, referencing the <strong className="text-slate-800">Campaign ID</strong></>,
      <>They will also be notified via the daily Teams summary, which shows all campaigns that are ready for activation</>,
    ],
  },
]

const VIDEOS: VideoGuide[] = [
  { title: 'Importing Media Plan', description: 'How to import from MAP', src: '/api/static/content-tracker/about-videos/Importing media plan.mp4', channel: 'both' },
  { title: 'Archiving Campaigns', description: 'Managing your working view', src: '/api/static/content-tracker/about-videos/Archiving Campaigns .mp4', channel: 'both' },
]

function StepList({ steps, color = 'blue' }: { steps: typeof SOCIAL_STEPS; color?: string }) {
  return (
    <div className="space-y-0">
      {steps.map(({ step, title, items }, i, arr) => (
        <div key={step} className="flex gap-4">
          <div className="flex flex-col items-center">
            <div className={`w-7 h-7 rounded-full bg-${color}-100 text-${color}-800 text-xs font-bold flex items-center justify-center shrink-0 z-10`}>
              {step}
            </div>
            {i < arr.length - 1 && (
              <div className="w-px flex-1 bg-slate-200 my-1.5" />
            )}
          </div>
          <div className={`min-w-0 flex-1 ${i < arr.length - 1 ? 'pb-6' : ''}`}>
            <p className="text-sm font-semibold text-slate-800 mt-0.5 mb-2">{title}</p>
            <ul className="space-y-1.5">
              {items.map((item, j) => (
                <li key={j} className="flex gap-2 text-sm text-slate-500 leading-relaxed">
                  <span className="text-blue-200 shrink-0 mt-0.5">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ))}
    </div>
  )
}

function SocialContent() {
  return (
    <>
      {/* Workflow Steps */}
      <Card className="shadow-none border-slate-300">
        <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">Process</p>
        </div>
        <CardHeader className="pb-4">
          <CardTitle className="text-xl">Your 3-step process</CardTitle>
        </CardHeader>
        <CardContent>
          <StepList steps={SOCIAL_STEPS} />
        </CardContent>
      </Card>

      {/* Platform & Format Table */}
      <Card className="shadow-none border-slate-300">
        <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">Formats</p>
        </div>
        <CardHeader className="pb-4">
          <CardTitle className="text-xl">Supported platform & format combinations</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="border-t border-slate-100 overflow-hidden rounded-b-xl">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  <th className="text-left px-6 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Platform</th>
                  <th className="text-left px-6 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Format Types</th>
                  <th className="text-left px-6 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Placement</th>
                  <th className="text-left px-6 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Sizes</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Meta', 'Image, Video, Carousel, Dynamic Creative, Collection Ad, Flexible Ad', 'In-feed, Reels, Stories', '1x1, 9x16'],
                  ['LinkedIn', 'Image, Video, Carousel, Document Ad', 'In-feed', '1x1'],
                  ['Snapchat', 'Image, Video, Story Ad', 'Stories', '9x16'],
                  ['TikTok', 'Video', 'In-feed', '9x16'],
                ].map(([platform, formats, placement, sizes], i) => (
                  <tr key={platform} className={`border-b border-slate-100 last:border-0 ${i % 2 === 1 ? 'bg-slate-50/40' : ''}`}>
                    <td className="px-6 py-4 font-medium text-slate-800">{platform}</td>
                    <td className="px-6 py-4 text-slate-500">{formats}</td>
                    <td className="px-6 py-4 text-slate-500">{placement}</td>
                    <td className="px-6 py-4 text-slate-500">{sizes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Row Splitting */}
      <Card className="shadow-none border-slate-300">
        <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">How it works</p>
        </div>
        <CardHeader className="pb-4">
          <CardTitle className="text-xl">How row splitting works</CardTitle>
          <CardDescription className="leading-relaxed mt-1">
            When you approve a media plan, Content Tracker splits each plan row into individual creative rows based on combinations of Nameplate, Format Type, Format Size, and Platform.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="border-l-[3px] border-blue-300 pl-4 py-1 space-y-1.5">
            <p className="text-sm font-semibold text-slate-800">Meta: Format Sizes are Grouped</p>
            <p className="text-sm text-slate-500 leading-relaxed">1x1 and 9x16 sizes use the same copy and activation setup, so they are grouped into a single row.</p>
            <span className="inline-block text-xs font-mono bg-blue-100 text-blue-800 px-2 py-1 rounded mt-0.5">
              Nameplates × Format Types = rows
            </span>
          </div>
          <div className="border-l-[3px] border-blue-200 pl-4 py-1 space-y-1.5">
            <p className="text-sm font-semibold text-slate-800">LinkedIn (and Others): Sizes are Split</p>
            <p className="text-sm text-slate-500 leading-relaxed">Each format size requires a separate activation setup, so each combination generates its own row.</p>
            <span className="inline-block text-xs font-mono bg-blue-100 text-blue-800 px-2 py-1 rounded mt-0.5">
              Nameplates × Format Types × Format Sizes = rows
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Making Updates to Live Campaigns */}
      <Card className="shadow-none border-slate-300">
        <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">Live updates</p>
        </div>
        <CardHeader className="pb-4">
          <CardTitle className="text-xl">Making updates to live campaigns</CardTitle>
        </CardHeader>
        <CardContent>
          <StepList steps={SOCIAL_LIVE_STEPS} />
        </CardContent>
      </Card>

      {/* Reddit Campaigns */}
      <Card className="shadow-none border-slate-300">
        <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">Special case</p>
        </div>
        <CardHeader className="pb-4">
          <CardTitle className="text-xl">Reddit campaigns</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="border-l-[3px] border-blue-300 pl-4 py-0.5">
            <p className="text-sm text-slate-700 leading-relaxed">
              As a planner, you should <strong className="text-slate-800">archive all rows related to Reddit campaigns</strong> since Reddit templates are not included in the tracker.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Archiving */}
      <Card className="shadow-none border-slate-300">
        <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">Archive</p>
        </div>
        <CardHeader className="pb-4">
          <CardTitle className="text-xl">Archiving rows</CardTitle>
          <CardDescription className="leading-relaxed mt-1">
            An optional tool to keep your working view clean and suppress unnecessary notifications. It does not delete any data.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-widest text-blue-800 mb-2.5">When to archive</p>
            <ul className="space-y-2">
              {[
                'A media plan has been cancelled or is no longer needed',
                'Rows were created from an incorrect plan and should not be progressed',
                'A campaign or placement has been confirmed as not required',
              ].map((item) => (
                <li key={item} className="flex gap-2 text-sm text-slate-500 leading-relaxed">
                  <span className="text-blue-200 shrink-0 mt-0.5">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="border-t border-slate-100 pt-5">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-blue-800 mb-2.5">How to archive / unarchive</p>
            <ul className="space-y-2">
              {[
                <>Open the row → click <strong className="text-slate-700">Status Overlay</strong> → scroll down and click <strong className="text-slate-700">Archive</strong></>,
                <>To unarchive: apply filter <strong className="text-slate-700">"Show archived rows"</strong> → open row → click <strong className="text-slate-700">Unarchive</strong></>,
              ].map((item, i) => (
                <li key={i} className="flex gap-2 text-sm text-slate-500 leading-relaxed">
                  <span className="text-blue-200 shrink-0 mt-0.5">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </CardContent>
      </Card>
    </>
  )
}

function SearchContent() {
  return (
    <>
      {/* Workflow Steps */}
      <Card className="shadow-none border-slate-300">
        <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">Process</p>
        </div>
        <CardHeader className="pb-4">
          <CardTitle className="text-xl">Your 3-step process</CardTitle>
        </CardHeader>
        <CardContent>
          <StepList steps={SEARCH_STEPS} />
        </CardContent>
      </Card>

      {/* Platform & Format Table */}
      <Card className="shadow-none border-slate-300">
        <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">Formats</p>
        </div>
        <CardHeader className="pb-4">
          <CardTitle className="text-xl">Supported platform & format types</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="border-t border-slate-100 overflow-hidden rounded-b-xl">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  <th className="text-left px-6 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Platform</th>
                  <th className="text-left px-6 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Format Types</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Google', 'Search, Google Performance Max, Demand Gen, YouTube View (Buy type = CPV), YouTube Reach (Buy type = CPM)'],
                  ['Microsoft', 'Search, Microsoft Performance Max, MSAN'],
                ].map(([platform, formats], i) => (
                  <tr key={platform} className={`border-b border-slate-100 last:border-0 ${i % 2 === 1 ? 'bg-slate-50/40' : ''}`}>
                    <td className="px-6 py-4 font-medium text-slate-800">{platform}</td>
                    <td className="px-6 py-4 text-slate-500">{formats}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Row Splitting */}
      <Card className="shadow-none border-slate-300">
        <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">How it works</p>
        </div>
        <CardHeader className="pb-4">
          <CardTitle className="text-xl">How row splitting works</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="border-l-[3px] border-blue-300 pl-4 py-1 space-y-1.5">
            <p className="text-sm text-slate-700 leading-relaxed">Rows are split by <strong className="text-slate-800">Nameplate</strong>.</p>
          </div>
        </CardContent>
      </Card>

      {/* Versioning & Locking */}
      <Card className="shadow-none border-slate-300">
        <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">Updates to live campaigns</p>
        </div>
        <CardHeader className="pb-4">
          <CardTitle className="text-xl">Versioning</CardTitle>
          <CardDescription className="leading-relaxed mt-1">
            Versioning ensures that live campaigns are tracked, preserved, and safely updated without overwriting existing work.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-widest text-blue-800 mb-2.5">Locking logic</p>
            <p className="text-sm text-slate-500 leading-relaxed mb-2">When a campaign goes live, the current row/version is:</p>
            <ul className="space-y-1.5">
              <li className="flex gap-2 text-sm text-slate-500 leading-relaxed">
                <span className="text-blue-200 shrink-0 mt-0.5">•</span>
                <span><strong className="text-slate-800">Locked</strong> and no further edits allowed</span>
              </li>
            </ul>
            <p className="text-sm text-slate-500 leading-relaxed mt-3">This ensures:</p>
            <ul className="space-y-1.5">
              <li className="flex gap-2 text-sm text-slate-500 leading-relaxed">
                <span className="text-blue-200 shrink-0 mt-0.5">•</span>
                <span>Historical accuracy</span>
              </li>
              <li className="flex gap-2 text-sm text-slate-500 leading-relaxed">
                <span className="text-blue-200 shrink-0 mt-0.5">•</span>
                <span>Traceability of what was live</span>
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Making Updates */}
      <Card className="shadow-none border-slate-300">
        <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">Live updates</p>
        </div>
        <CardHeader className="pb-4">
          <CardTitle className="text-xl">Making updates to live campaigns</CardTitle>
        </CardHeader>
        <CardContent>
          <StepList steps={SEARCH_LIVE_STEPS} />
        </CardContent>
      </Card>

      {/* Archiving */}
      <Card className="shadow-none border-slate-300">
        <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">Archive</p>
        </div>
        <CardHeader className="pb-4">
          <CardTitle className="text-xl">Archiving rows</CardTitle>
          <CardDescription className="leading-relaxed mt-1">
            An optional tool to keep your working view clean and suppress unnecessary notifications. It does not delete any data.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-widest text-blue-800 mb-2.5">When to archive</p>
            <ul className="space-y-2">
              {[
                'A media plan has been cancelled or is no longer needed',
                'Rows were created from an incorrect plan and should not be progressed',
                'A campaign or placement has been confirmed as not required',
              ].map((item) => (
                <li key={item} className="flex gap-2 text-sm text-slate-500 leading-relaxed">
                  <span className="text-blue-200 shrink-0 mt-0.5">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="border-t border-slate-100 pt-5">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-blue-800 mb-2.5">How to archive / unarchive</p>
            <ul className="space-y-2">
              {[
                <>Open the row → click <strong className="text-slate-700">Status Overlay</strong> → scroll down and click <strong className="text-slate-700">Archive</strong></>,
                <>To unarchive: apply filter <strong className="text-slate-700">"Show archived rows"</strong> → open row → click <strong className="text-slate-700">Unarchive</strong></>,
              ].map((item, i) => (
                <li key={i} className="flex gap-2 text-sm text-slate-500 leading-relaxed">
                  <span className="text-blue-200 shrink-0 mt-0.5">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </CardContent>
      </Card>
    </>
  )
}

export function PlannerTab() {
  const [channel, setChannel] = useState<Channel>('social');

  return (
    <div className="flex gap-8 max-w-6xl mx-auto">
      <div className="flex-1 min-w-0 space-y-6">

        {/* Channel Toggle */}
        <ChannelToggle value={channel} onChange={setChannel} color="blue" />

        {/* Intro */}
        <Card className="shadow-none border-slate-300">
          <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">Planner</p>
          </div>
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
                <ClipboardList className="w-5 h-5 text-blue-800" strokeWidth={1.75} />
              </div>
              <CardTitle className="text-xl">Planner Guidelines</CardTitle>
            </div>
            <CardDescription className="leading-relaxed mt-1">
              Finalise media plans in MAP and validate the rows created in Content Tracker.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border-l-[3px] border-blue-300 pl-4 py-0.5">
              <p className="text-sm text-slate-700 leading-relaxed">
                Your job is to get the media plan right in MAP. Content Tracker rows are created automatically from your input —
                you do not manually create rows. Rows are generated when you finalise and approve a media plan.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Channel-specific content */}
        {channel === 'social' ? <SocialContent /> : <SearchContent />}

      </div>

      <div className="w-64 flex-shrink-0 sticky top-20 self-start">
        <VideoGuidePanel videos={VIDEOS} />
      </div>
    </div>
  )
}
